import React, { useState, useEffect } from 'react'
import {
  ChartBarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { normalizeAnalyticsDashboardData } from '../../utils/normalizeApiData'

function Analytics() {
  const [timeRange, setTimeRange] = useState('7d')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [retryCount, setRetryCount] = useState(0)

  // Fetch analytics data from the API whenever the selected range changes
  useEffect(() => {
    let cancelled = false

    const fetchAnalytics = async () => {
      try {
        setLoading(true)
        setError(null)
        const res = await authFetch(getLocalApiUrl(`/api/analytics/dashboard?range=${timeRange}`))
        if (!res.ok) throw new Error(`Server error: ${res.status}`)
        const json = await res.json()
        if (!json.success) throw new Error(json.error || 'Unknown error')
        if (!cancelled) setData(normalizeAnalyticsDashboardData(json.data))
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load analytics')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchAnalytics()
    return () => { cancelled = true }
  }, [timeRange, retryCount])

  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K'
    }
    return num.toString()
  }

  const formatTrend = (trend) => {
    const isPositive = trend > 0
    return (
      <div className={`flex items-center ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? (
          <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
        ) : (
          <ArrowTrendingDownIcon className="h-4 w-4 mr-1" />
        )}
        <span className="text-sm font-medium">
          {Math.abs(trend).toFixed(1)}%
        </span>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-psg-teal mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Analytics Dashboard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">{error}</p>
          <button
            onClick={() => setRetryCount(c => c + 1)}
            className="mt-4 btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (!data) return null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-sm text-gray-500">
            Monitor rule performance and alert trends
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="select-field"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Alerts</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(data.overview.totalAlerts)}</p>
              <p className="text-xs text-gray-500">{data.overview.alertsToday} today</p>
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            </div>
          </div>
          <div className="mt-4">
            {formatTrend(data.overview.alertsTrend)}
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Rules</p>
              <p className="text-2xl font-bold text-gray-900">{data.overview.activeRules}</p>
              <p className="text-xs text-gray-500">monitoring</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <ChartBarIcon className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4">
            {formatTrend(data.overview.rulesTrend)}
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Response Time</p>
              <p className="text-2xl font-bold text-gray-900">{data.overview.avgResponseTime}m</p>
              <p className="text-xs text-gray-500">to resolution</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <ClockIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4">
            {formatTrend(data.overview.responseTrend)}
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">False Positive Rate</p>
              <p className="text-2xl font-bold text-gray-900">{data.overview.falsePositiveRate}%</p>
              <p className="text-xs text-gray-500">of all alerts</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-full">
              <XCircleIcon className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
          <div className="mt-4">
            {formatTrend(data.overview.fpTrend)}
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Alerts by Category */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Alerts by Category</h3>
          <div className="space-y-5">
            {data.alertsByCategory.map((item) => (
              <div key={item.category} className="flex items-center justify-between py-1">
                <div className="flex items-center space-x-3 min-w-0 flex-1">
                  <div className={`w-4 h-4 rounded flex-shrink-0 ${item.color}`}></div>
                  <span className="text-sm font-medium text-gray-900 truncate">{item.category}</span>
                </div>
                <div className="flex items-center space-x-3 ml-4">
                  <span className="text-sm text-gray-500 w-8 text-right">{item.count}</span>
                  <div className="w-20 bg-gray-200 rounded-full h-2 flex-shrink-0">
                    <div 
                      className={`h-2 rounded-full ${item.color}`}
                      style={{ width: `${item.percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-gray-900 w-10 text-right">
                    {item.percentage}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Alerts by Severity */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Alerts by Severity</h3>
          <div className="space-y-5">
            {data.alertsBySeverity.map((item) => (
              <div key={item.severity} className="flex items-center justify-between py-1">
                <div className="flex items-center space-x-3 min-w-0 flex-1">
                  <div className={`w-4 h-4 rounded flex-shrink-0 ${item.color}`}></div>
                  <span className="text-sm font-medium text-gray-900 truncate">{item.severity}</span>
                </div>
                <div className="flex items-center space-x-3 ml-4">
                  <span className="text-sm text-gray-500 w-8 text-right">{item.count}</span>
                  <div className="w-20 bg-gray-200 rounded-full h-2 flex-shrink-0">
                    <div 
                      className={`h-2 rounded-full ${item.color}`}
                      style={{ width: `${item.percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-gray-900 w-10 text-right">
                    {item.percentage}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Time Series and Top Rules */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Time Series Chart */}
        <div className="lg:col-span-2 card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Alert Trends</h3>
          <div className="h-80 flex items-end justify-between space-x-1 px-2">
            {data.timeSeriesData.map((day, index) => {
              const maxValue = Math.max(...data.timeSeriesData.map(d => d.alerts + d.resolved))
              const totalHeight = 240 // Available height for bars
              const alertHeight = (day.alerts / maxValue) * totalHeight
              const resolvedHeight = (day.resolved / maxValue) * totalHeight
              
              return (
                <div key={index} className="flex-1 flex flex-col items-center min-w-0">
                  <div className="w-full flex flex-col items-center relative" style={{ height: `${totalHeight}px` }}>
                    {/* Alert bar */}
                    <div 
                      className="w-full bg-red-400 rounded-t absolute bottom-0"
                      style={{ 
                        height: `${alertHeight + resolvedHeight}px`,
                        maxWidth: '24px',
                        margin: '0 auto'
                      }}
                      title={`${day.alerts} alerts`}
                    ></div>
                    {/* Resolved bar */}
                    <div 
                      className="w-full bg-green-400 rounded-b absolute bottom-0"
                      style={{ 
                        height: `${resolvedHeight}px`,
                        maxWidth: '24px',
                        margin: '0 auto'
                      }}
                      title={`${day.resolved} resolved`}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-500 mt-3 text-center truncate w-full">
                    {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                </div>
              )
            })}
          </div>
          <div className="flex items-center justify-center space-x-6 mt-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-400 rounded"></div>
              <span className="text-sm text-gray-600">Alerts</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-400 rounded"></div>
              <span className="text-sm text-gray-600">Resolved</span>
            </div>
          </div>
        </div>

        {/* Top Performing Rules */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Top Rules by Triggers</h3>
          <div className="space-y-3">
            {data.topRules.map((rule, index) => (
              <div key={rule.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-900">#{index + 1}</span>
                    <span className="text-sm text-gray-600 truncate">{rule.name}</span>
                  </div>
                  <div className="text-xs text-gray-500">{rule.id}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">{rule.triggers}</div>
                  <div className="text-xs text-red-500">{rule.falsePositives} FP</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">System Performance</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {data.performanceMetrics.avgProcessingTime}ms
            </div>
            <div className="text-sm text-gray-500">Avg Processing</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">
              {data.performanceMetrics.peakProcessingTime}ms
            </div>
            <div className="text-sm text-gray-500">Peak Processing</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {formatNumber(data.performanceMetrics.throughput)}/h
            </div>
            <div className="text-sm text-gray-500">Throughput</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {data.performanceMetrics.systemLoad}%
            </div>
            <div className="text-sm text-gray-500">System Load</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">
              {data.performanceMetrics.memoryUsage}%
            </div>
            <div className="text-sm text-gray-500">Memory Usage</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-teal-600">
              {data.performanceMetrics.cacheHitRate}%
            </div>
            <div className="text-sm text-gray-500">Cache Hit Rate</div>
          </div>
        </div>
      </div>

      {/* Alert Types Distribution */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Alert Distribution by Rule Type</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {data.alertsByType.map((type) => (
            <div key={type.type} className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 mb-2">{type.count}</div>
              <div className="text-sm font-medium text-gray-700 mb-1">{type.type}</div>
              <div className="text-xs text-gray-500">{type.percentage}% of total</div>
              <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-primary-500 h-2 rounded-full"
                  style={{ width: `${type.percentage}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Export and Actions */}
      <div className="flex justify-end space-x-4">
        <button className="btn-secondary">
          Export Report
        </button>
        <button className="btn-secondary">
          Schedule Report
        </button>
        <button className="btn-primary">
          Generate Insights
        </button>
      </div>
    </div>
  )
}

export default Analytics
